/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../styles/planVacaciones.css';

import {
  Grid,
  Typography,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';

import Heading from '../components/heading/Heading';
import Item from '../components/custom/Item';
import Formatter from '../scripts/formatters';
import msgNotificacion from '../scripts/notification';
import SolicitudesDataGrid from '../components/styledDataGrid/SolicitudesDataGrid';
import CustomNoRowsOlverlay from '../components/custom/CustomNoRowsOlverlay';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DoneIcon from '@mui/icons-material/Done';
import BlockIcon from '@mui/icons-material/Block';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';

import FormPlanVacacionesEmpleadoPdf from '../components/reactPDF/FormPlanVacacionesEmpleadoPdf';
import FormReporteDepartamentoPlanVacacionPdf from '../components/reactPDF/FormReporteDepartamentoPlanVacacionPdf';

import { useDispatch } from 'react-redux';
import { showProgress } from '../redux/reducers/progress.Slice';
import { NAME_PROJECT } from '../redux/constants/index';

import PlanVacacionesService from '../services/planVacacionesService';
import FeriadosService from '../services/feriadosService';
import { mapaFeriadosDesdeLista } from '../utils/feriados';

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const JORNADA_LABEL = { C: 'Día completo', M: 'Medio día (AM)', T: 'Medio día (PM)' };
const JORNADA_CYCLE = [null, 'C', 'M', 'T'];
const JORNADA_CYCLE_SABADO = [null, 'M'];

const esSabado = (date) => date.getDay() === 6;
const esDomingo = (date) => date.getDay() === 0;

const normalizarJornadaDia = (date, tipo) => {
  if (!tipo) return null;
  if (esSabado(date)) return 'M';
  return tipo;
};

const siguienteJornada = (date, actual) => {
  const ciclo = esSabado(date) ? JORNADA_CYCLE_SABADO : JORNADA_CYCLE;
  const idx = ciclo.indexOf(actual);
  return ciclo[(idx + 1) % ciclo.length];
};

const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Fecha desde API/SQL sin corrimiento por zona horaria (evita 01-jun → 31-may). */
const fechaApiToKey = (value) => {
  if (value == null || value === '') return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

const totalDiasEquivalentes = (diasMap) =>
  Object.values(diasMap).reduce((acc, tipo) => acc + (tipo === 'C' ? 1 : 0.5), 0);

/** Días que se recomienda no planificar (reserva de emergencia). No bloquea guardar/enviar. */
const DIAS_EMERGENCIA_RECOMENDADOS = 4;

/** Período por defecto en Mi plan: Abierto; si no hay, el más reciente (lista viene DESC por fecha). */
const elegirPeriodoMiPlan = (periodos) => {
  const abierto = periodos.find((p) => p.IdEstadoPeriodo === 2);
  if (abierto) return String(abierto.IdPeriodo);
  return periodos.length ? String(periodos[0].IdPeriodo) : '';
};

/** Bandeja: período Abierto; si no hay, todos (''). */
const elegirPeriodoBandeja = (periodos) => {
  const abierto = periodos.find((p) => p.IdEstadoPeriodo === 2);
  return abierto ? String(abierto.IdPeriodo) : '';
};

const diasMapDesdeApi = (dias) => {
  const map = {};
  (dias || []).forEach((d) => {
    const key = fechaApiToKey(d.Fecha);
    if (!key) return;
    const date = new Date(`${key}T12:00:00`);
    const tipo = normalizarJornadaDia(date, d.TipoJornada);
    if (tipo) map[key] = tipo;
  });
  return map;
};

const buildCalendarioCells = (mesVisible) => {
  const year = mesVisible.getFullYear();
  const month = mesVisible.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

  const cells = [];
  const cursor = new Date(start);

  while (true) {
    if (!esDomingo(cursor)) {
      cells.push(new Date(cursor));
    }
    if (cursor >= last && cells.length > 0 && cells.length % 6 === 0) break;
    cursor.setDate(cursor.getDate() + 1);
    if (cursor.getTime() - first.getTime() > 50 * 24 * 60 * 60 * 1000) break;
  }

  return cells;
};

const periodoDesdeFechas = (fechaInicio, fechaFin) => ({
  FechaInicio: fechaInicio,
  FechaFin: fechaFin,
});

const enRangoFechasPeriodo = (date, periodo) => {
  if (!periodo?.FechaInicio) return false;
  const key = toDateKey(date);
  const ini = fechaApiToKey(periodo.FechaInicio) || String(periodo.FechaInicio).slice(0, 10);
  const fin = fechaApiToKey(periodo.FechaFin) || String(periodo.FechaFin).slice(0, 10);
  return key >= ini && key <= fin;
};

const periodoCalendarioDesde = (periodo) => {
  const ini = fechaApiToKey(periodo?.FechaInicio) || String(periodo?.FechaInicio || '').slice(0, 10);
  if (!ini) return null;
  const [y, m] = ini.split('-').map(Number);
  return new Date(y, m - 1, 1);
};

const periodoCalendarioHasta = (periodo) => {
  const fin = fechaApiToKey(periodo?.FechaFin) || String(periodo?.FechaFin || '').slice(0, 10);
  if (!fin) return null;
  const [y, m] = fin.split('-').map(Number);
  return new Date(y, m - 1, 1);
};

const indiceMes = (date) => date.getFullYear() * 12 + date.getMonth();

const acotarMesAlPeriodo = (mes, periodo) => {
  const min = periodoCalendarioDesde(periodo);
  const max = periodoCalendarioHasta(periodo);
  if (!min || !max || !mes) return mes;
  const idx = indiceMes(mes);
  if (idx < indiceMes(min)) return min;
  if (idx > indiceMes(max)) return max;
  return new Date(mes.getFullYear(), mes.getMonth(), 1);
};

const puedeIrMesAnterior = (mesVisible, periodo) => {
  const min = periodoCalendarioDesde(periodo);
  if (!min || !mesVisible) return false;
  return indiceMes(mesVisible) > indiceMes(min);
};

const puedeIrMesSiguiente = (mesVisible, periodo) => {
  const max = periodoCalendarioHasta(periodo);
  if (!max || !mesVisible) return false;
  return indiceMes(mesVisible) < indiceMes(max);
};

const cambiarMesEnPeriodo = (mesVisible, delta, periodo) =>
  acotarMesAlPeriodo(new Date(mesVisible.getFullYear(), mesVisible.getMonth() + delta, 1), periodo);

const nombreDiaSemana = (fechaKey) => {
  const date = new Date(`${fechaKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-ES', { weekday: 'long' });
};

const filasReporteDesdeMapa = (diasMap) =>
  Object.entries(diasMap)
    .map(([fecha, tipo]) => ({
      id: fecha,
      fecha,
      diaSemana: nombreDiaSemana(fecha),
      tipoLabel: JORNADA_LABEL[tipo] || tipo,
      diasEquivalentes: tipo === 'C' ? 1 : 0.5,
    }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));

const JORNADA_ABREV = { C: 'C', M: 'AM', T: 'PM' };

const nombrePanelCeldaFallback = (nombre) => {
  const partes = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '—';
  if (partes.length === 1) return partes[0];
  if (partes.length === 2) return `${partes[0]} ${partes[1]}`;
  if (partes.length === 3) return `${partes[0]} ${partes[1]}`;
  return `${partes[0]} ${partes[partes.length - 2]}`;
};

/** Nombre corto para celdas: NOMBRE_PILA + PRIMER_APELLIDO (Softland), con fallback al parseo de NOMBRE */
const nombrePanelCelda = (row) => {
  const pila = String(row?.NombrePila ?? '').trim();
  const primerApellido = String(row?.PrimerApellido ?? '').trim();
  if (pila && primerApellido) return `${pila} ${primerApellido}`;
  if (pila) return pila;
  if (primerApellido) return primerApellido;
  return nombrePanelCeldaFallback(row?.NombreEmpleado);
};

const panelEntradasPorFecha = (filas) => {
  const map = {};
  (filas || []).forEach((row) => {
    const key = fechaApiToKey(row.Fecha);
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push({
      idPlan: row.IdPlan,
      codEmpleado: row.CodEmpleado,
      nombre: row.NombreEmpleado,
      nombreCorto: nombrePanelCelda(row),
      tipo: row.TipoJornada,
      tipoAbrev: JORNADA_ABREV[row.TipoJornada] || row.TipoJornada,
      estado: row.Estado,
      codDepartamento: row.CodDepartamento,
      nombreDepartamento: row.NombreDepartamento,
      periodoFechaInicio: row.PeriodoFechaInicio,
      periodoFechaFin: row.PeriodoFechaFin,
      nombrePeriodo: row.NombrePeriodo,
    });
  });
  Object.values(map).forEach((lista) => {
    lista.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
  });
  return map;
};

const totalVacacionesDisponiblesPlan = (planData) => {
  if (!planData) return null;
  if (planData.TotalVacacionesDisponibles != null && planData.TotalVacacionesDisponibles !== '') {
    return Number(planData.TotalVacacionesDisponibles);
  }
  const saldo = planData.VacacionesPendientesInicio != null ? Number(planData.VacacionesPendientesInicio) : 0;
  const proporcion = planData.VacacionesProporcionPeriodo != null ? Number(planData.VacacionesProporcionPeriodo) : 0;
  return saldo + proporcion;
};

const periodoFilaToData = (row) => ({
  idPeriodo: row.IdPeriodo,
  nombre: row.Nombre,
  fechaInicio: fechaApiToKey(row.FechaInicio) || String(row.FechaInicio || '').slice(0, 10),
  fechaFin: fechaApiToKey(row.FechaFin) || String(row.FechaFin || '').slice(0, 10),
});

function TabPanel ({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function PlanVacaciones () {
  const dispatch = useDispatch();
  const userLogin = JSON.parse(localStorage.getItem('user'));
  const puedeGestionarPeriodos = [1, 2].includes(Number(userLogin?.CodigoRol));

  const [tab, setTab] = useState('mi-plan');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSelected, setPeriodoSelected] = useState('');
  const [periodoBandeja, setPeriodoBandeja] = useState('');
  const [periodosInicializados, setPeriodosInicializados] = useState(false);
  const [plan, setPlan] = useState(null);
  const [diasMap, setDiasMap] = useState({});
  const [diasBloqueados, setDiasBloqueados] = useState({});
  const [diaReagendarOrigen, setDiaReagendarOrigen] = useState(null);
  const [modalReagendar, setModalReagendar] = useState({
    open: false,
    fechaOrigen: null,
    fechaDestino: null,
    tipoOrigen: null,
    tipoJornada: 'C',
    esSabadoOrigen: false,
    esSabadoDestino: false,
  });
  const [motivoPlan, setMotivoPlan] = useState('');
  const [mesVisible, setMesVisible] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadingBandeja, setLoadingBandeja] = useState(false);
  const [vistaMiPlan, setVistaMiPlan] = useState('calendario');
  const [printReporte, setPrintReporte] = useState(false);
  const [printPayload, setPrintPayload] = useState(null);
  const [printReporteDepto, setPrintReporteDepto] = useState(false);
  const [printReporteDeptoPayload, setPrintReporteDeptoPayload] = useState(null);

  const [planesBandeja, setPlanesBandeja] = useState([]);
  const [vistaBandeja, setVistaBandeja] = useState('listado');
  const [panelDatos, setPanelDatos] = useState([]);
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [departamentoPanel, setDepartamentoPanel] = useState('');
  const [mesPanel, setMesPanel] = useState(new Date());
  const [modalRechazo, setModalRechazo] = useState({ open: false, idPlan: null, comentario: '' });
  const [modalVerPlan, setModalVerPlan] = useState({
    open: false,
    plan: null,
    diasMap: {},
    mesVisible: new Date(),
    empleadoLabel: '',
    periodo: null,
  });

  const [formPeriodo, setFormPeriodo] = useState({
    idPeriodo: null,
    nombre: '',
    fechaInicio: '',
    fechaFin: '',
  });

  const [feriadosMap, setFeriadosMap] = useState({});
  const [feriadosMapPanel, setFeriadosMapPanel] = useState({});
  const [feriadosMapModal, setFeriadosMapModal] = useState({});

  const periodoActivo = useMemo(
    () => periodos.find((p) => String(p.IdPeriodo) === String(periodoSelected)),
    [periodos, periodoSelected]
  );

  const periodoPanelActivo = useMemo(
    () => periodos.find((p) => String(p.IdPeriodo) === String(periodoBandeja)),
    [periodos, periodoBandeja]
  );

  const panelFiltrado = useMemo(() => {
    if (!departamentoPanel) return panelDatos;
    return panelDatos.filter((r) => String(r.CodDepartamento) === String(departamentoPanel));
  }, [panelDatos, departamentoPanel]);

  const planesBandejaFiltrados = useMemo(() => {
    if (!departamentoPanel) return planesBandeja;
    return planesBandeja.filter((r) => String(r.CodDepartamento) === String(departamentoPanel));
  }, [planesBandeja, departamentoPanel]);

  const panelPorFecha = useMemo(() => panelEntradasPorFecha(panelFiltrado), [panelFiltrado]);

  const departamentosBandeja = useMemo(() => {
    const map = new Map();
    const agregar = (cod, nombre) => {
      if (cod == null || cod === '') return;
      const key = String(cod);
      if (!map.has(key)) map.set(key, nombre || key);
    };
    planesBandeja.forEach((r) => agregar(r.CodDepartamento, r.NombreDepartamento));
    panelDatos.forEach((r) => agregar(r.CodDepartamento, r.NombreDepartamento));
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [planesBandeja, panelDatos]);

  const diasPanelMes = useMemo(() => buildCalendarioCells(mesPanel), [mesPanel]);

  const periodoCalendario = useMemo(() => {
    if (periodoActivo?.FechaInicio) {
      return periodoDesdeFechas(periodoActivo.FechaInicio, periodoActivo.FechaFin);
    }
    if (plan?.PeriodoFechaInicio) {
      return periodoDesdeFechas(plan.PeriodoFechaInicio, plan.PeriodoFechaFin);
    }
    return null;
  }, [periodoActivo, plan?.PeriodoFechaInicio, plan?.PeriodoFechaFin]);

  const puedeEditarPlan = plan && [0, 3].includes(plan.IdEstado) && periodoActivo?.IdEstadoPeriodo === 2;
  const puedeReagendarPlan = plan && Number(plan.IdEstado) === 2;

  const diasMarcados = useMemo(() => totalDiasEquivalentes(diasMap), [diasMap]);
  const filasReporte = useMemo(() => filasReporteDesdeMapa(diasMap), [diasMap]);
  const saldoAntesPeriodo = plan?.VacacionesPendientesInicio != null
    ? Number(plan.VacacionesPendientesInicio)
    : null;
  const proporcionPeriodo = plan?.VacacionesProporcionPeriodo != null
    ? Number(plan.VacacionesProporcionPeriodo)
    : null;
  const vacacionesDisponibles = plan ? totalVacacionesDisponiblesPlan(plan) : null;
  const disponibleTrasPlan = vacacionesDisponibles != null ? vacacionesDisponibles - diasMarcados : null;
  const excedeVacaciones = vacacionesDisponibles != null && diasMarcados > vacacionesDisponibles;
  const alertaDiasEmergencia = vacacionesDisponibles != null
    && diasMarcados > vacacionesDisponibles - DIAS_EMERGENCIA_RECOMENDADOS
    && !excedeVacaciones;

  const validarTopeVacaciones = () => {
    if (vacacionesDisponibles == null) return null;
    if (diasMarcados > vacacionesDisponibles) {
      return `El plan (${diasMarcados} días) excede el total disponible (${vacacionesDisponibles.toFixed(2)} = saldo antes ${(saldoAntesPeriodo ?? 0).toFixed(2)} + proporción ${(proporcionPeriodo ?? 0).toFixed(2)}).`;
    }
    return null;
  };

  const cargarFeriadosPorPeriodo = async (periodo, setter) => {
    const ini = fechaApiToKey(periodo?.FechaInicio);
    const fin = fechaApiToKey(periodo?.FechaFin);
    if (!ini || !fin) {
      setter({});
      return;
    }
    try {
      const response = await FeriadosService.getFeriados({ fechaInicio: ini, fechaFin: fin });
      if (response?.Codigo === 1) {
        setter(mapaFeriadosDesdeLista(response.Data));
      } else {
        setter({});
      }
    } catch (e) {
      console.error(e);
      setter({});
    }
  };

  const esFeriadoEnMapa = (mapa, date) => Boolean(mapa[toDateKey(date)]);
  const nombreFeriadoEnMapa = (mapa, date) => mapa[toDateKey(date)];

  const loadPeriodos = async () => {
    const response = await PlanVacacionesService.getPeriodos();
    if (response?.Codigo === 1) {
      const data = response.Data || [];
      setPeriodos(data);
      if (!periodosInicializados) {
        setPeriodoSelected(elegirPeriodoMiPlan(data));
        setPeriodoBandeja(elegirPeriodoBandeja(data));
        setPeriodosInicializados(true);
      }
    }
    return response;
  };

  const loadPlan = async (idPeriodo) => {
    if (!idPeriodo) return;
    setLoading(true);
    try {
      const response = await PlanVacacionesService.getPlanEmpleado({
        idPeriodo: Number(idPeriodo),
        codEmpleado: userLogin.CodigoEmpleado,
      });
      if (response?.Codigo === 1) {
        const { plan: planData, dias, diasBloqueados: bloqueos } = response.Data || {};
        setPlan(planData);
        setMotivoPlan(planData?.Motivo || '');
        setDiasMap(diasMapDesdeApi(dias));
        setDiasBloqueados(bloqueos || {});
        setDiaReagendarOrigen(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadBandeja = async () => {
    setLoadingBandeja(true);
    try {
      const response = await PlanVacacionesService.getPlanes({
        codEmpleado: userLogin.CodigoEmpleado,
        idPeriodo: periodoBandeja ? Number(periodoBandeja) : null,
      });
      if (response?.Codigo === 1) {
        setPlanesBandeja(response.Data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingBandeja(false);
    }
  };

  const loadPanel = async () => {
    if (!periodoBandeja) {
      setPanelDatos([]);
      return;
    }
    setLoadingPanel(true);
    try {
      const response = await PlanVacacionesService.getPanelPlanVacacion({
        codEmpleado: userLogin.CodigoEmpleado,
        idPeriodo: Number(periodoBandeja),
      });
      if (response?.Codigo === 1) {
        setPanelDatos(response.Data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPanel(false);
    }
  };

  useEffect(() => {
    document.title = `${NAME_PROJECT} | Plan de Vacaciones`;
    loadPeriodos();
  }, []);

  useEffect(() => {
    if (tab === 'mi-plan' && periodoSelected) {
      loadPlan(periodoSelected);
    }
  }, [periodoSelected]);

  useEffect(() => {
    if (!periodoCalendario?.FechaInicio) return;
    const inicio = periodoCalendarioDesde(periodoCalendario);
    if (inicio) setMesVisible(inicio);
  }, [periodoSelected, periodoCalendario?.FechaInicio, periodoCalendario?.FechaFin]);

  useEffect(() => {
    if (tab === 'mi-plan' && periodoCalendario?.FechaInicio) {
      cargarFeriadosPorPeriodo(periodoCalendario, setFeriadosMap);
    }
  }, [tab, periodoCalendario?.FechaInicio, periodoCalendario?.FechaFin]);

  useEffect(() => {
    if (tab === 'aprobaciones' && periodoPanelActivo?.FechaInicio) {
      cargarFeriadosPorPeriodo(periodoPanelActivo, setFeriadosMapPanel);
    }
  }, [tab, periodoPanelActivo?.FechaInicio, periodoPanelActivo?.FechaFin]);

  useEffect(() => {
    if (modalVerPlan.open && modalVerPlan.periodo?.FechaInicio) {
      cargarFeriadosPorPeriodo(modalVerPlan.periodo, setFeriadosMapModal);
    }
  }, [modalVerPlan.open, modalVerPlan.periodo?.FechaInicio, modalVerPlan.periodo?.FechaFin]);

  useEffect(() => {
    if (tab === 'aprobaciones') {
      loadBandeja();
      if (vistaBandeja === 'panel') {
        loadPanel();
      }
    }
  }, [periodoBandeja]);

  useEffect(() => {
    if (tab === 'aprobaciones' && vistaBandeja === 'panel') {
      loadPanel();
    }
  }, [vistaBandeja]);

  useEffect(() => {
    if (!periodoPanelActivo?.FechaInicio) return;
    const inicio = periodoCalendarioDesde(periodoPanelActivo);
    if (inicio) setMesPanel(inicio);
  }, [periodoBandeja, periodoPanelActivo?.FechaInicio, periodoPanelActivo?.FechaFin]);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    if (newTab === 'mi-plan' && periodoSelected) {
      loadPlan(periodoSelected);
    } else if (newTab === 'aprobaciones') {
      loadBandeja();
      if (vistaBandeja === 'panel') {
        loadPanel();
      }
    } else if (newTab === 'periodos') {
      loadPeriodos();
    }
  };

  const abrirVerPlan = async (row) => {
    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.getPlanEmpleado({
        idPeriodo: row.IdPeriodo,
        codEmpleado: row.CodEmpleado,
      });
      if (response?.Codigo !== 1) {
        msgNotificacion(response?.Mensaje || 'No se pudo cargar el plan', 'warning');
        return;
      }
      const { plan: planData, dias } = response.Data || {};
      const periodo = periodoDesdeFechas(row.PeriodoFechaInicio, row.PeriodoFechaFin);
      const ini = fechaApiToKey(row.PeriodoFechaInicio) || fechaApiToKey(planData?.PeriodoFechaInicio);
      const mesInicial = acotarMesAlPeriodo(
        ini ? new Date(`${ini}T12:00:00`) : new Date(),
        periodo
      );
      setModalVerPlan({
        open: true,
        plan: planData,
        diasMap: diasMapDesdeApi(dias),
        mesVisible: mesInicial,
        empleadoLabel: `${row.CodEmpleado} - ${row.NombreEmpleado}`,
        periodo,
      });
    } catch (e) {
      console.error(e);
      msgNotificacion('Error al cargar el plan', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const abrirVerPlanDesdePanel = async (entry) => {
    const row = planesBandeja.find((p) => p.IdPlan === entry.idPlan);
    if (row) {
      abrirVerPlan(row);
      return;
    }
    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.getPlanEmpleado({
        idPeriodo: Number(periodoBandeja),
        codEmpleado: entry.codEmpleado,
      });
      if (response?.Codigo !== 1) {
        msgNotificacion(response?.Mensaje || 'No se pudo cargar el plan', 'warning');
        return;
      }
      const { plan: planData, dias } = response.Data || {};
      const periodo = periodoDesdeFechas(entry.periodoFechaInicio, entry.periodoFechaFin);
      const ini = fechaApiToKey(entry.periodoFechaInicio);
      const mesInicial = acotarMesAlPeriodo(
        ini ? new Date(`${ini}T12:00:00`) : new Date(),
        periodo
      );
      setModalVerPlan({
        open: true,
        plan: planData,
        diasMap: diasMapDesdeApi(dias),
        mesVisible: mesInicial,
        empleadoLabel: `${entry.codEmpleado} - ${entry.nombre}`,
        periodo,
      });
    } catch (e) {
      console.error(e);
      msgNotificacion('Error al cargar el plan', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const enRangoPeriodoPanel = useCallback(
    (date) => enRangoFechasPeriodo(date, periodoPanelActivo),
    [periodoPanelActivo]
  );

  const diasEnMes = useMemo(() => buildCalendarioCells(mesVisible), [mesVisible]);

  const modalDiasEnMes = useMemo(
    () => (modalVerPlan.open ? buildCalendarioCells(modalVerPlan.mesVisible) : []),
    [modalVerPlan.open, modalVerPlan.mesVisible]
  );

  const enRangoPeriodo = useCallback(
    (date) => enRangoFechasPeriodo(date, periodoActivo),
    [periodoActivo]
  );

  const limpiarDia = (date) => {
    if (esDomingo(date) || esFeriadoEnMapa(feriadosMap, date)) return;
    if (!puedeEditarPlan || !enRangoPeriodo(date)) return;
    const key = toDateKey(date);
    setDiasMap((prev) => {
      if (!prev[key]) return prev;
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const toggleDia = (date) => {
    if (!puedeEditarPlan || !enRangoPeriodo(date) || esDomingo(date)) return;
    if (esFeriadoEnMapa(feriadosMap, date)) {
      msgNotificacion(`No se puede planificar: ${nombreFeriadoEnMapa(feriadosMap, date)} (feriado).`, 'warning');
      return;
    }
    const key = toDateKey(date);
    const actual = diasMap[key] || null;
    const next = siguienteJornada(date, actual);
    setDiasMap((prev) => {
      const copy = { ...prev };
      if (!next) delete copy[key];
      else copy[key] = next;
      return copy;
    });
  };

  const handleClickDiaCalendario = async (date, e) => {
    if (puedeEditarPlan) {
      if (e.ctrlKey || e.metaKey) {
        limpiarDia(date);
        return;
      }
      toggleDia(date);
      return;
    }

    if (!puedeReagendarPlan) return;
    if (!enRangoPeriodo(date) || esDomingo(date)) return;
    if (esFeriadoEnMapa(feriadosMap, date)) {
      msgNotificacion(`No se puede reagendar a un feriado: ${nombreFeriadoEnMapa(feriadosMap, date)}.`, 'warning');
      return;
    }

    const key = toDateKey(date);
    const tipo = diasMap[key];
    const bloqueo = diasBloqueados[key];

    if (bloqueo) {
      msgNotificacion(`No se puede reagendar el ${key}: ${bloqueo.motivo}.`, 'warning');
      return;
    }

    if (!diaReagendarOrigen) {
      if (!tipo) {
        msgNotificacion('Seleccione primero un día planificado para moverlo o cambiar jornada.', 'info');
        return;
      }
      setDiaReagendarOrigen(key);
      msgNotificacion(`Día ${key} seleccionado. Elija un día libre, o vuelva a clicarlo para solo cambiar la jornada.`, 'info');
      return;
    }

    if (diaReagendarOrigen === key) {
      const tipoOrigen = diasMap[key];
      setModalReagendar({
        open: true,
        fechaOrigen: key,
        fechaDestino: key,
        tipoOrigen,
        tipoJornada: esSabado(date) ? 'M' : tipoOrigen,
        esSabadoOrigen: esSabado(date),
        esSabadoDestino: esSabado(date),
      });
      return;
    }

    if (tipo) {
      msgNotificacion('Ese día ya está planificado. Elija un día libre o cancele la selección.', 'warning');
      return;
    }

    const tipoOrigen = diasMap[diaReagendarOrigen];
    const origenEsSabado = esSabado(new Date(`${diaReagendarOrigen}T12:00:00`));
    const destinoEsSabado = esSabado(date);
    setModalReagendar({
      open: true,
      fechaOrigen: diaReagendarOrigen,
      fechaDestino: key,
      tipoOrigen,
      /* Sábado → día hábil: por defecto día completo */
      tipoJornada: destinoEsSabado ? 'M' : origenEsSabado ? 'C' : tipoOrigen,
      esSabadoOrigen: origenEsSabado,
      esSabadoDestino: destinoEsSabado,
    });
  };

  const cerrarModalReagendar = () => {
    setModalReagendar({
      open: false,
      fechaOrigen: null,
      fechaDestino: null,
      tipoOrigen: null,
      tipoJornada: 'C',
      esSabadoOrigen: false,
      esSabadoDestino: false,
    });
  };

  const confirmarReagendar = async () => {
    const { fechaOrigen, fechaDestino, tipoOrigen, tipoJornada, esSabadoOrigen, esSabadoDestino } = modalReagendar;
    if (!fechaOrigen || !fechaDestino || !plan?.IdPlan) return;

    let tipoFinal = tipoJornada;
    if (esSabadoDestino) tipoFinal = 'M';

    const eqOrigen = tipoOrigen === 'C' ? 1 : 0.5;
    const eqDestino = tipoFinal === 'C' ? 1 : 0.5;
    const puedeSubirACompleto = esSabadoOrigen && !esSabadoDestino && tipoFinal === 'C';
    if (eqDestino > eqOrigen && !puedeSubirACompleto) {
      msgNotificacion(
        'No se puede pasar de medio día a día completo, salvo al mover un sábado a un día hábil.',
        'warning'
      );
      return;
    }

    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.reagendarDiaPlan({
        idPlan: plan.IdPlan,
        fechaOrigen,
        fechaDestino,
        tipoJornadaDestino: tipoFinal,
        codEmpleado: userLogin.CodigoEmpleado,
      });
      if (response?.Codigo === 1) {
        msgNotificacion(response.Mensaje || 'Día reagendado', 'success');
        setDiaReagendarOrigen(null);
        cerrarModalReagendar();
        await loadPlan(periodoSelected);
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo reagendar', 'warning');
      }
    } catch (error) {
      console.error(error);
      msgNotificacion('Error al reagendar el día', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const persistirDias = async () => {
    if (!plan?.IdPlan) return { Codigo: 0, Mensaje: 'Plan no disponible' };

    const dias = Object.entries(diasMap)
      .map(([fecha, tipoJornada]) => {
        const date = new Date(`${fecha}T12:00:00`);
        return {
          fecha,
          tipoJornada: normalizarJornadaDia(date, tipoJornada),
        };
      })
      .filter((d) => d.tipoJornada);

    return PlanVacacionesService.guardarDias({ idPlan: plan.IdPlan, dias });
  };

  const handleGuardarDias = async () => {
    const errorTope = validarTopeVacaciones();
    if (errorTope) {
      msgNotificacion(errorTope, 'warning');
      return false;
    }

    dispatch(showProgress(true));
    try {
      const response = await persistirDias();
      if (response?.Codigo === 1) {
        msgNotificacion('Plan guardado', 'success');
        await loadPlan(periodoSelected);
        return true;
      }
      msgNotificacion(response?.Mensaje || 'No se pudo guardar', 'warning');
      return false;
    } catch (error) {
      console.error(error);
      msgNotificacion('Error al guardar los días del plan', 'error');
      return false;
    } finally {
      dispatch(showProgress(false));
    }
  };

  const handleEnviarPlan = async () => {
    if (!plan?.IdPlan) return;

    const errorTope = validarTopeVacaciones();
    if (errorTope) {
      msgNotificacion(errorTope, 'warning');
      return;
    }

    dispatch(showProgress(true));
    try {
      const responseGuardar = await persistirDias();
      if (responseGuardar?.Codigo !== 1) {
        msgNotificacion(responseGuardar?.Mensaje || 'No se pudieron guardar los días', 'warning');
        return;
      }

      const response = await PlanVacacionesService.enviarPlan({
        idPlan: plan.IdPlan,
        motivo: motivoPlan,
      });
      if (response?.Codigo === 1) {
        msgNotificacion('Plan enviado a aprobación', 'success');
        await loadPlan(periodoSelected);
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo enviar', 'warning');
      }
    } catch (error) {
      console.error(error);
      msgNotificacion('Error al enviar el plan', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const handleGuardarPeriodo = async (accion = 'GUARDAR', periodoOverride = null) => {
    if (!puedeGestionarPeriodos) {
      msgNotificacion('Solo Admin o RRHH pueden gestionar períodos', 'warning');
      return;
    }

    const data = periodoOverride || formPeriodo;

    if (accion === 'REFRESCAR_SALDOS') {
      if (!data.idPeriodo) {
        msgNotificacion('Período no válido para refrescar saldos', 'warning');
        return;
      }
    } else if (!data.nombre || !data.fechaInicio || !data.fechaFin) {
      msgNotificacion('Complete nombre y fechas del período', 'warning');
      return;
    }

    if (accion === 'ABRIR') {
      const otroAbierto = periodos.find(
        (p) => p.IdEstadoPeriodo === 2 && Number(p.IdPeriodo) !== Number(data.idPeriodo)
      );
      if (otroAbierto) {
        msgNotificacion(
          `Ya hay un período abierto (${otroAbierto.Nombre}). Ciérrelo antes de abrir otro.`,
          'warning'
        );
        return;
      }
    }

    if (accion === 'GUARDAR' && data.idPeriodo) {
      const periodoExistente = periodos.find((p) => p.IdPeriodo === data.idPeriodo);
      if (periodoExistente && periodoExistente.IdEstadoPeriodo !== 1) {
        msgNotificacion(
          'Solo puede cambiar nombre y fechas mientras el período está en Borrador. Si ya está Abierto, use Cerrar.',
          'warning'
        );
        return;
      }
    }

    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.guardarPeriodo({
        idPeriodo: data.idPeriodo,
        nombre: data.nombre,
        fechaInicio: data.fechaInicio,
        fechaFin: data.fechaFin,
        idUsuario: userLogin.CodigoUsuario,
        codigoRol: userLogin.CodigoRol,
        accion,
      });
      if (response?.Codigo === 1) {
        msgNotificacion(
          accion === 'ABRIR'
            ? 'Período abierto'
            : accion === 'CERRAR'
              ? 'Período cerrado'
              : accion === 'REFRESCAR_SALDOS'
                ? 'Saldos actualizados para todos los empleados del período'
                : 'Período guardado',
          'success'
        );
        if (accion !== 'REFRESCAR_SALDOS') {
          setFormPeriodo({ idPeriodo: null, nombre: '', fechaInicio: '', fechaFin: '' });
        }
        await loadPeriodos();
      } else {
        msgNotificacion(response?.Mensaje || 'Error al guardar período', 'warning');
      }
    } finally {
      dispatch(showProgress(false));
    }
  };

  const handleRefrescarSaldosPeriodo = async (idPeriodo) => {
    if (!idPeriodo) {
      msgNotificacion('Seleccione un período', 'warning');
      return;
    }
    const periodo = periodos.find((p) => String(p.IdPeriodo) === String(idPeriodo));
    if (!periodo) {
      msgNotificacion('Período no encontrado', 'warning');
      return;
    }
    if (Number(periodo.IdEstadoPeriodo) !== 2) {
      msgNotificacion('Solo se puede refrescar saldos en períodos abiertos', 'warning');
      return;
    }
    await handleGuardarPeriodo('REFRESCAR_SALDOS', periodoFilaToData(periodo));
  };

  const handleAprobar = async (idPlan) => {
    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.aprobarPlan({
        idPlan,
        idUsuario: userLogin.CodigoUsuario,
      });
      if (response?.Codigo === 1) {
        msgNotificacion(response.Mensaje, 'success');
        loadBandeja();
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo aprobar', 'warning');
      }
    } finally {
      dispatch(showProgress(false));
    }
  };

  const confirmarRechazo = async () => {
    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.rechazarPlan({
        idPlan: modalRechazo.idPlan,
        idUsuario: userLogin.CodigoUsuario,
        comentario: modalRechazo.comentario,
      });
      if (response?.Codigo === 1) {
        msgNotificacion('Plan rechazado', 'success');
        setModalRechazo({ open: false, idPlan: null, comentario: '' });
        loadBandeja();
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo rechazar', 'warning');
      }
    } finally {
      dispatch(showProgress(false));
    }
  };

  const imprimirPlan = useCallback((planData, diasMapData) => {
    if (!planData) {
      msgNotificacion('No hay datos del plan para imprimir', 'warning');
      return;
    }
    setPrintPayload({
      plan: { ...planData },
      data: filasReporteDesdeMapa(diasMapData),
    });
    setPrintReporte(true);
  }, []);

  const imprimirPlanDesdeFila = async (row) => {
    dispatch(showProgress(true));
    try {
      const response = await PlanVacacionesService.getPlanEmpleado({
        idPeriodo: row.IdPeriodo,
        codEmpleado: row.CodEmpleado,
      });
      if (response?.Codigo !== 1) {
        msgNotificacion(response?.Mensaje || 'No se pudo cargar el plan', 'warning');
        return;
      }
      const { plan: planData, dias } = response.Data || {};
      imprimirPlan(planData, diasMapDesdeApi(dias));
    } catch (e) {
      console.error(e);
      msgNotificacion('Error al cargar el plan para imprimir', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const cargarReporteDepartamento = async () => {
    if (!puedeGestionarPeriodos) {
      msgNotificacion('Solo Admin o RRHH pueden generar el reporte por departamento', 'warning');
      return null;
    }
    if (!periodoBandeja) {
      msgNotificacion('Seleccione un período específico', 'warning');
      return null;
    }
    if (!departamentoPanel) {
      msgNotificacion('Seleccione un departamento para el reporte', 'warning');
      return null;
    }

    const response = await PlanVacacionesService.getReporteDepartamento({
      codEmpleado: userLogin.CodigoEmpleado,
      idPeriodo: Number(periodoBandeja),
      codDepartamento: departamentoPanel,
      codigoRol: userLogin.CodigoRol,
    });

    if (response?.Codigo !== 1) {
      msgNotificacion(response?.Mensaje || 'No se pudo generar el reporte', 'warning');
      return null;
    }

    const data = response.Data || {};
    if (!(data.empleados || []).length) {
      msgNotificacion('No hay planes en ese departamento y período', 'warning');
      return null;
    }

    return data;
  };

  const imprimirReporteDepartamento = async () => {
    dispatch(showProgress(true));
    try {
      const data = await cargarReporteDepartamento();
      if (!data) return;
      setPrintReporteDeptoPayload(data);
      setPrintReporteDepto(true);
    } catch (e) {
      console.error(e);
      msgNotificacion('Error al generar el reporte', 'error');
    } finally {
      dispatch(showProgress(false));
    }
  };

  const columnsBandeja = [
    { field: 'IdPlan', headerName: 'ID', width: 70 },
    { field: 'CodEmpleado', headerName: 'Código', width: 90 },
    { field: 'NombreEmpleado', headerName: 'Empleado', minWidth: 160, flex: 1 },
    { field: 'NombreDepartamento', headerName: 'Departamento', minWidth: 140, flex: 1 },
    {
      field: 'VacacionesPendientesInicio',
      headerName: 'Saldo antes',
      width: 95,
      valueFormatter: (value) => (value != null && value !== '' ? Formatter.currencyFormatter(value) : '—'),
    },
    {
      field: 'VacacionesProporcionPeriodo',
      headerName: 'Proporción',
      width: 95,
      valueFormatter: (value) => (value != null && value !== '' ? Formatter.currencyFormatter(value) : '—'),
    },
    {
      field: 'TotalVacacionesDisponibles',
      headerName: 'Total disp.',
      width: 95,
      valueFormatter: (value) => (value != null && value !== '' ? Formatter.currencyFormatter(value) : '—'),
    },
    { field: 'TotalDiasEquivalentes', headerName: 'Días eq.', width: 90 },
    {
      field: 'SaldoRestante',
      headerName: 'Saldo restante',
      width: 110,
      valueGetter: (_value, row) => {
        const total = Number(row.TotalVacacionesDisponibles);
        const dias = Number(row.TotalDiasEquivalentes);
        if (Number.isNaN(total) && Number.isNaN(dias)) return null;
        return (Number.isNaN(total) ? 0 : total) - (Number.isNaN(dias) ? 0 : dias);
      },
      valueFormatter: (value) => (value != null && value !== '' ? Formatter.currencyFormatter(value) : '—'),
    },
    {
      field: 'FechaEnvio',
      headerName: 'Fecha envío',
      width: 130,
      valueFormatter: (value) => (value ? Formatter.dateTimeFormatter(value) : '—'),
    },
    {
      field: 'FechaAprobacion',
      headerName: 'Fecha aprobación',
      width: 140,
      valueFormatter: (value) => (value ? Formatter.dateTimeFormatter(value) : '—'),
    },
    { field: 'Estado', headerName: 'Estado', width: 110 },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 240,
      sortable: false,
      renderCell: ({ row }) => {
        const esSubordinado = Number(row.EsSubordinado) === 1;
        const esPropio = Number(row.EsPropio) === 1;
        const esJefePropio = Number(row.EsJefePropio) === 1;
        const puedeGestionar = esSubordinado || (esPropio && esJefePropio);
        const periodoAbierto = Number(row.IdEstadoPeriodo) === 2;
        const puedeAprobar = row.IdEstado === 1 && periodoAbierto && puedeGestionar;
        const puedeRechazar = row.IdEstado === 1 && periodoAbierto && puedeGestionar;
        return (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Ver plan">
            <IconButton size="small" color="primary" onClick={() => abrirVerPlan(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Imprimir PDF">
            <IconButton size="small" color="secondary" onClick={() => imprimirPlanDesdeFila(row)}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {puedeAprobar && (
            <Tooltip title="Aprobar">
              <IconButton size="small" color="success" onClick={() => handleAprobar(row.IdPlan)}>
                <DoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {puedeRechazar && (
            <Tooltip title="Rechazar">
              <IconButton
                size="small"
                color="error"
                onClick={() => setModalRechazo({ open: true, idPlan: row.IdPlan, comentario: '' })}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        );
      },
    },
  ];

  return (
    <Grid container spacing={1} paddingX={1} paddingTop={1}>
      <Grid item xs={12}>
        <Heading title="Plan de Vacaciones" />
      </Grid>

      <Grid item xs={12}>
        <Item>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Mi plan" value="mi-plan" />
            <Tab label="Aprobaciones" value="aprobaciones" />
            {puedeGestionarPeriodos && <Tab label="Períodos (Admin / RRHH)" value="periodos" />}
          </Tabs>

          <Box sx={{ px: 2, pb: 2 }}>
            <TabPanel value={tab} index="mi-plan">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Período</InputLabel>
                    <Select
                      label="Período"
                      value={periodoSelected}
                      onChange={(e) => setPeriodoSelected(e.target.value)}
                    >
                      {periodos.map((p) => (
                        <MenuItem key={p.IdPeriodo} value={String(p.IdPeriodo)}>
                          {p.Nombre} ({p.EstadoPeriodo})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  {plan && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <Chip label={`Estado: ${plan.Estado}`} size="small" color="primary" variant="outlined" />
                      {saldoAntesPeriodo != null && (
                        <Chip
                          label={`Saldo antes: ${saldoAntesPeriodo.toFixed(2)}`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      )}
                      {proporcionPeriodo != null && (
                        <Chip
                          label={`Proporción: ${proporcionPeriodo.toFixed(2)}`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      {vacacionesDisponibles != null && (
                        <Chip
                          label={`Total disp.: ${vacacionesDisponibles.toFixed(2)}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      <Chip
                        label={`Días planificados: ${diasMarcados.toFixed(2)}`}
                        size="small"
                        variant="outlined"
                        color={excedeVacaciones ? 'error' : 'default'}
                      />
                      {disponibleTrasPlan != null && (
                        <Chip
                          label={`Quedarían: ${disponibleTrasPlan.toFixed(2)}`}
                          size="small"
                          variant="outlined"
                          color={disponibleTrasPlan < 0 ? 'error' : 'default'}
                        />
                      )}
                      {plan.ComentarioRechazo && (
                        <Typography variant="body2" color="error">
                          Rechazo: {plan.ComentarioRechazo}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  </Stack>
                </Grid>

                {vacacionesDisponibles != null && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Saldo antes del período: <strong>{(saldoAntesPeriodo ?? 0).toFixed(2)}</strong>
                      {' '}| Proporción: <strong>{(proporcionPeriodo ?? 0).toFixed(2)}</strong>
                      {' '}| Total disponible: <strong>{vacacionesDisponibles.toFixed(2)}</strong>.
                      No puede planificar más que ese total.
                      Se recomienda dejar {DIAS_EMERGENCIA_RECOMENDADOS} días sin planificar.
                    </Typography>
                    {excedeVacaciones && (
                      <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                        El plan supera el total disponible. Reduzca días antes de guardar o enviar.
                      </Typography>
                    )}
                    {alertaDiasEmergencia && (
                      <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                        Está planificando más de lo recomendado; intente dejar {DIAS_EMERGENCIA_RECOMENDADOS} días de reserva.
                      </Typography>
                    )}
                  </Grid>
                )}

                {periodoActivo?.IdEstadoPeriodo !== 2 && (
                  <Grid item xs={12}>
                    <Typography color="warning.main">
                      El período seleccionado no está abierto. Solo puede consultar su plan.
                    </Typography>
                  </Grid>
                )}

                {puedeReagendarPlan && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="info.main">
                      Su plan está aprobado. Puede reagendar días (mover a otra fecha) sin agregar ni quitar.
                      Los días con solicitud aprobada o aplicada quedan bloqueados.
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={vistaMiPlan}
                      onChange={(_, value) => value && setVistaMiPlan(value)}
                    >
                      <ToggleButton value="calendario">Calendario</ToggleButton>
                      <ToggleButton value="reporte">Reporte</ToggleButton>
                    </ToggleButtonGroup>
                    {vistaMiPlan === 'reporte' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => imprimirPlan({ ...plan, Motivo: motivoPlan }, diasMap)}
                        disabled={!plan}
                      >
                        Imprimir PDF
                      </Button>
                    )}
                  </Stack>
                </Grid>

                {vistaMiPlan === 'calendario' && (
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <IconButton
                      disabled={!puedeIrMesAnterior(mesVisible, periodoCalendario)}
                      onClick={() => setMesVisible((prev) => cambiarMesEnPeriodo(prev, -1, periodoCalendario))}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography fontWeight="bold">
                      {mesVisible.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </Typography>
                    <IconButton
                      disabled={!puedeIrMesSiguiente(mesVisible, periodoCalendario)}
                      onClick={() => setMesVisible((prev) => cambiarMesEnPeriodo(prev, 1, periodoCalendario))}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>

                  <div className="plan-vacaciones-legend">
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-C" /> Día completo
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-M" /> Medio día AM
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-T" /> Medio día PM
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch feriado" /> Feriado
                    </span>
                    {puedeReagendarPlan && (
                      <span className="plan-vacaciones-legend-item">
                        <span className="plan-vacaciones-legend-swatch bloqueado" /> Bloqueado (solicitud aprobada/aplicada)
                      </span>
                    )}
                    {puedeEditarPlan && (
                      <Typography variant="caption" color="text.secondary">
                        Clic derecho o Ctrl+clic en un día marcado para quitarlo.
                      </Typography>
                    )}
                    {puedeReagendarPlan && (
                      <Typography variant="caption" color="text.secondary">
                        Plan aprobado: clic en un día, luego en un día libre (elige jornada). Segundo clic en el mismo día = solo cambiar jornada. No se puede aumentar el total del plan.
                      </Typography>
                    )}
                  </div>

                  {puedeReagendarPlan && diaReagendarOrigen && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip
                        color="primary"
                        label={`Moviendo: ${diaReagendarOrigen}`}
                        onDelete={() => setDiaReagendarOrigen(null)}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Elija el nuevo día en el calendario
                      </Typography>
                    </Stack>
                  )}

                  <div className="plan-vacaciones-calendar">
                    {WEEKDAYS.map((w) => (
                      <div key={w} className="plan-vacaciones-weekday">{w}</div>
                    ))}
                    {diasEnMes.map((date) => {
                      const key = toDateKey(date);
                      const tipo = diasMap[key];
                      const inMonth = date.getMonth() === mesVisible.getMonth();
                      const inRange = enRangoPeriodo(date);
                      const esFeriado = esFeriadoEnMapa(feriadosMap, date);
                      const bloqueado = Boolean(diasBloqueados[key]);
                      const seleccionado = diaReagendarOrigen === key;
                      const interactivo = (puedeEditarPlan || puedeReagendarPlan) && inRange && !esFeriado && !esDomingo(date);
                      const classes = [
                        'plan-vacaciones-day',
                        !inMonth ? 'out-month' : '',
                        !interactivo || (puedeReagendarPlan && bloqueado) ? 'disabled' : '',
                        esFeriado ? 'feriado' : '',
                        bloqueado ? 'bloqueado' : '',
                        seleccionado ? 'seleccionado-reagendar' : '',
                        tipo ? `tipo-${tipo}` : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <div
                          key={key}
                          className={classes}
                          title={
                            esFeriado
                              ? nombreFeriadoEnMapa(feriadosMap, date)
                              : bloqueado
                                ? diasBloqueados[key]?.motivo
                                : undefined
                          }
                          onClick={(e) => handleClickDiaCalendario(date, e)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (puedeEditarPlan && tipo) limpiarDia(date);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (puedeEditarPlan && (e.key === 'Delete' || e.key === 'Backspace')) limpiarDia(date);
                          }}
                        >
                          <div className="plan-vacaciones-day-number">{date.getDate()}</div>
                          {tipo && <div className="plan-vacaciones-day-label">{JORNADA_LABEL[tipo]}</div>}
                          {bloqueado && <div className="plan-vacaciones-day-lock">Bloq.</div>}
                        </div>
                      );
                    })}
                  </div>
                </Grid>
                )}

                {vistaMiPlan === 'reporte' && (
                  <Grid item xs={12}>
                    <SolicitudesDataGrid
                      rows={filasReporte}
                      columns={[
                        { field: 'fecha', headerName: 'Fecha', width: 120, valueFormatter: (v) => Formatter.dateFormatter(v) },
                        { field: 'diaSemana', headerName: 'Día', width: 120 },
                        { field: 'tipoLabel', headerName: 'Jornada', minWidth: 160, flex: 1 },
                        {
                          field: 'diasEquivalentes',
                          headerName: 'Días eq.',
                          width: 90,
                          valueFormatter: (v) => Formatter.currencyFormatter(v),
                        },
                      ]}
                      getRowId={(row) => row.id}
                      autoHeight
                      density="compact"
                      localeText={Formatter.localizedTextsMapColumns}
                      slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {vacacionesDisponibles != null ? (
                        <>
                          <strong>Saldo antes:</strong> {(saldoAntesPeriodo ?? 0).toFixed(2)}
                          {' '}
                          + <strong>Proporción:</strong> {(proporcionPeriodo ?? 0).toFixed(2)}
                          {' '}
                          = <strong>Total disp.:</strong> {vacacionesDisponibles.toFixed(2)}
                          {' '}
                          - <strong>Total planificado:</strong> {diasMarcados.toFixed(2)}
                          {' '}
                          = <strong>Quedarían:</strong> {disponibleTrasPlan.toFixed(2)}
                        </>
                      ) : (
                        <>
                          <strong>Total planificado:</strong> {diasMarcados.toFixed(2)}
                        </>
                      )}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Motivo / observaciones del plan"
                    value={motivoPlan}
                    onChange={(e) => setMotivoPlan(e.target.value)}
                    disabled={!puedeEditarPlan}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      disabled={!puedeEditarPlan || loading || excedeVacaciones}
                      onClick={handleGuardarDias}
                    >
                      Guardar
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={!puedeEditarPlan || loading || excedeVacaciones}
                      onClick={handleEnviarPlan}
                    >
                      Enviar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tab} index="aprobaciones">
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Período</InputLabel>
                    <Select
                      label="Período"
                      value={periodoBandeja}
                      onChange={(e) => setPeriodoBandeja(e.target.value)}
                    >
                      <MenuItem value="">Todos los períodos</MenuItem>
                      {periodos.map((p) => (
                        <MenuItem key={p.IdPeriodo} value={String(p.IdPeriodo)}>
                          {p.Nombre} ({p.EstadoPeriodo})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Departamento</InputLabel>
                    <Select
                      label="Departamento"
                      value={departamentoPanel}
                      onChange={(e) => setDepartamentoPanel(e.target.value)}
                      disabled={loadingBandeja}
                    >
                      <MenuItem value="">Todos (mi alcance)</MenuItem>
                      {departamentosBandeja.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ pt: { xs: 0, md: 0.5 } }}>
                    {puedeGestionarPeriodos && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        disabled={!periodoBandeja || !departamentoPanel || loadingBandeja}
                        onClick={imprimirReporteDepartamento}
                      >
                        Reporte dept.
                      </Button>
                    )}
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={vistaBandeja}
                      onChange={(_, value) => value && setVistaBandeja(value)}
                    >
                      <ToggleButton value="listado">Listado</ToggleButton>
                      <ToggleButton value="panel">Panel equipo</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                      {vistaBandeja === 'listado'
                        ? 'Use el ojo para ver el calendario de cada empleado.'
                        : 'Calendario consolidado de quienes aparecen en su listado.'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {vistaBandeja === 'listado' ? (
                <SolicitudesDataGrid
                  rows={planesBandejaFiltrados}
                  columns={columnsBandeja}
                  getRowId={(row) => row.IdPlan}
                  autoHeight
                  loading={loadingBandeja}
                  localeText={Formatter.localizedTextsMapColumns}
                  slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
                  density="compact"
                />
              ) : !periodoBandeja ? (
                <Typography color="warning.main" sx={{ py: 2 }}>
                  Seleccione un período específico para ver el panel consolidado.
                </Typography>
              ) : (
                <Box sx={{ opacity: loadingPanel ? 0.6 : 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <IconButton
                      disabled={!puedeIrMesAnterior(mesPanel, periodoPanelActivo)}
                      onClick={() => setMesPanel((prev) => cambiarMesEnPeriodo(prev, -1, periodoPanelActivo))}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {mesPanel.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                      {periodoPanelActivo?.Nombre ? ` · ${periodoPanelActivo.Nombre}` : ''}
                    </Typography>
                    <IconButton
                      disabled={!puedeIrMesSiguiente(mesPanel, periodoPanelActivo)}
                      onClick={() => setMesPanel((prev) => cambiarMesEnPeriodo(prev, 1, periodoPanelActivo))}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>

                  <div className="plan-vacaciones-legend">
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-C" /> Día completo
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-M" /> Medio día AM
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      <span className="plan-vacaciones-legend-swatch tipo-T" /> Medio día PM
                    </span>
                    <span className="plan-vacaciones-legend-item">
                      Clic en un nombre para ver su plan
                    </span>
                  </div>

                  <div className="plan-vacaciones-calendar plan-vacaciones-calendar-panel">
                    {WEEKDAYS.map((w) => (
                      <div key={w} className="plan-vacaciones-weekday">{w}</div>
                    ))}
                    {diasPanelMes.map((date) => {
                      const key = toDateKey(date);
                      const entradas = panelPorFecha[key] || [];
                      const inMonth = date.getMonth() === mesPanel.getMonth();
                      const inRange = enRangoPeriodoPanel(date);
                      const esFeriado = esFeriadoEnMapa(feriadosMapPanel, date);
                      const clases = ['plan-vacaciones-day', 'plan-vacaciones-day-panel'];
                      if (!inMonth) clases.push('out-month');
                      if (!inRange || esFeriado) clases.push('disabled');
                      if (esFeriado) clases.push('feriado');
                      if (entradas.length) clases.push('has-entries');

                      return (
                        <div
                          key={key}
                          className={clases.join(' ')}
                          title={esFeriado ? nombreFeriadoEnMapa(feriadosMapPanel, date) : undefined}
                        >
                          <div className="plan-vacaciones-day-number">{date.getDate()}</div>
                          {entradas.map((entrada) => (
                            <Tooltip
                              key={`${entrada.idPlan}-${key}`}
                              title={`${entrada.nombre} · ${JORNADA_LABEL[entrada.tipo] || entrada.tipo} · ${entrada.estado}`}
                            >
                              <button
                                type="button"
                                className={`plan-vacaciones-panel-entry tipo-${entrada.tipo}`}
                                onClick={() => abrirVerPlanDesdePanel(entrada)}
                              >
                                {entrada.nombreCorto} ({entrada.tipoAbrev})
                              </button>
                            </Tooltip>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {panelFiltrado.length === 0
                      ? 'No hay días planificados en su alcance para este período.'
                      : `${new Set(panelFiltrado.map((r) => r.CodEmpleado)).size} empleado(s) con días marcados.`}
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {puedeGestionarPeriodos && (
              <TabPanel value={tab} index="periodos">
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip size="small" variant="outlined" label="Borrador: editar y abrir" />
                  <Chip size="small" variant="outlined" color="success" label="Abierto: empleados planifican" />
                  <Chip size="small" variant="outlined" color="info" label="Refrescar saldos: solo si está abierto" />
                  <Chip size="small" variant="outlined" label="Cerrado: solo consulta" />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre del período"
                      value={formPeriodo.nombre}
                      onChange={(e) => setFormPeriodo({ ...formPeriodo, nombre: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Fecha inicio"
                      InputLabelProps={{ shrink: true }}
                      value={formPeriodo.fechaInicio}
                      onChange={(e) => setFormPeriodo({ ...formPeriodo, fechaInicio: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Fecha fin"
                      InputLabelProps={{ shrink: true }}
                      value={formPeriodo.fechaFin}
                      onChange={(e) => setFormPeriodo({ ...formPeriodo, fechaFin: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button variant="contained" onClick={() => handleGuardarPeriodo('GUARDAR')}>
                      {formPeriodo.idPeriodo ? 'Actualizar período' : 'Crear período'}
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <SolicitudesDataGrid
                      rows={periodos}
                      columns={[
                        { field: 'IdPeriodo', headerName: 'ID', width: 70 },
                        { field: 'Nombre', headerName: 'Nombre', minWidth: 160, flex: 1 },
                        {
                          field: 'FechaInicio',
                          headerName: 'Inicio',
                          width: 120,
                          valueFormatter: (v) => Formatter.dateFormatter(v),
                        },
                        {
                          field: 'FechaFin',
                          headerName: 'Fin',
                          width: 120,
                          valueFormatter: (v) => Formatter.dateFormatter(v),
                        },
                        { field: 'EstadoPeriodo', headerName: 'Estado', width: 100 },
                        { field: 'PlanesEnviados', headerName: 'Enviados', width: 90 },
                        {
                          field: 'acciones',
                          headerName: 'Acciones',
                          width: 240,
                          sortable: false,
                          renderCell: ({ row }) => (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {row.IdEstadoPeriodo === 1 && (
                                <Button
                                  size="small"
                                  onClick={() => setFormPeriodo(periodoFilaToData(row))}
                                >
                                  Editar
                                </Button>
                              )}
                              {row.IdEstadoPeriodo === 1 && (
                                <Button
                                  size="small"
                                  color="success"
                                  onClick={() => handleGuardarPeriodo('ABRIR', periodoFilaToData(row))}
                                >
                                  Abrir
                                </Button>
                              )}
                              {row.IdEstadoPeriodo === 2 && (
                                <>
                                  <Button
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => handleRefrescarSaldosPeriodo(row.IdPeriodo)}
                                  >
                                    Refrescar
                                  </Button>
                                  <Button
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onClick={() => handleGuardarPeriodo('CERRAR', periodoFilaToData(row))}
                                  >
                                    Cerrar
                                  </Button>
                                </>
                              )}
                            </Stack>
                          ),
                        },
                      ]}
                      getRowId={(row) => row.IdPeriodo}
                      autoHeight
                      localeText={Formatter.localizedTextsMapColumns}
                      slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
                      density="compact"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </Box>
        </Item>
      </Grid>

      <Dialog
        open={modalVerPlan.open}
        onClose={() =>
          setModalVerPlan({ open: false, plan: null, diasMap: {}, mesVisible: new Date(), empleadoLabel: '', periodo: null })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Plan de vacaciones — {modalVerPlan.empleadoLabel}
        </DialogTitle>
        <DialogContent>
          {modalVerPlan.plan && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Estado: ${modalVerPlan.plan.Estado}`} size="small" color="primary" variant="outlined" />
                {modalVerPlan.plan.VacacionesPendientesInicio != null && (
                  <Chip
                    label={`Saldo antes: ${Number(modalVerPlan.plan.VacacionesPendientesInicio).toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                )}
                {modalVerPlan.plan.VacacionesProporcionPeriodo != null && (
                  <Chip
                    label={`Proporción: ${Number(modalVerPlan.plan.VacacionesProporcionPeriodo).toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                )}
                {totalVacacionesDisponiblesPlan(modalVerPlan.plan) != null && (
                  <Chip
                    label={`Total disp.: ${totalVacacionesDisponiblesPlan(modalVerPlan.plan).toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                <Chip
                  label={`Días planificados: ${totalDiasEquivalentes(modalVerPlan.diasMap)}`}
                  size="small"
                  variant="outlined"
                />
                {modalVerPlan.plan.NombrePeriodo && (
                  <Chip label={modalVerPlan.plan.NombrePeriodo} size="small" variant="outlined" />
                )}
              </Stack>
              {modalVerPlan.plan.Motivo && (
                <Typography variant="body2">
                  <strong>Motivo:</strong> {modalVerPlan.plan.Motivo}
                </Typography>
              )}
              {modalVerPlan.plan.ComentarioRechazo && (
                <Typography variant="body2" color="error">
                  <strong>Rechazo:</strong> {modalVerPlan.plan.ComentarioRechazo}
                </Typography>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <IconButton
                  disabled={!puedeIrMesAnterior(modalVerPlan.mesVisible, modalVerPlan.periodo)}
                  onClick={() =>
                    setModalVerPlan((prev) => ({
                      ...prev,
                      mesVisible: cambiarMesEnPeriodo(prev.mesVisible, -1, prev.periodo),
                    }))
                  }
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography fontWeight="bold">
                  {modalVerPlan.mesVisible.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </Typography>
                <IconButton
                  disabled={!puedeIrMesSiguiente(modalVerPlan.mesVisible, modalVerPlan.periodo)}
                  onClick={() =>
                    setModalVerPlan((prev) => ({
                      ...prev,
                      mesVisible: cambiarMesEnPeriodo(prev.mesVisible, 1, prev.periodo),
                    }))
                  }
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>

              <div className="plan-vacaciones-legend">
                <span className="plan-vacaciones-legend-item">
                  <span className="plan-vacaciones-legend-swatch tipo-C" /> Día completo
                </span>
                <span className="plan-vacaciones-legend-item">
                  <span className="plan-vacaciones-legend-swatch tipo-M" /> Medio día AM
                </span>
                <span className="plan-vacaciones-legend-item">
                  <span className="plan-vacaciones-legend-swatch tipo-T" /> Medio día PM
                </span>
              </div>

              <div className="plan-vacaciones-calendar">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="plan-vacaciones-weekday">{w}</div>
                ))}
                {modalDiasEnMes.map((date) => {
                  const key = toDateKey(date);
                  const tipo = modalVerPlan.diasMap[key];
                  const inMonth = date.getMonth() === modalVerPlan.mesVisible.getMonth();
                  const inRange = enRangoFechasPeriodo(date, modalVerPlan.periodo);
                  const esFeriado = esFeriadoEnMapa(feriadosMapModal, date);
                  const classes = [
                    'plan-vacaciones-day',
                    !inMonth ? 'out-month' : '',
                    !inRange || esFeriado ? 'disabled' : '',
                    esFeriado ? 'feriado' : '',
                    tipo ? `tipo-${tipo}` : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <div
                      key={key}
                      className={classes}
                      title={esFeriado ? nombreFeriadoEnMapa(feriadosMapModal, date) : undefined}
                    >
                      <div className="plan-vacaciones-day-number">{date.getDate()}</div>
                      {tipo && <div className="plan-vacaciones-day-label">{JORNADA_LABEL[tipo]}</div>}
                    </div>
                  );
                })}
              </div>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={!modalVerPlan.plan}
            onClick={() => imprimirPlan(modalVerPlan.plan, modalVerPlan.diasMap)}
          >
            Imprimir PDF
          </Button>
          <Button
            onClick={() =>
              setModalVerPlan({ open: false, plan: null, diasMap: {}, mesVisible: new Date(), empleadoLabel: '', periodo: null })
            }
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalRechazo.open} onClose={() => setModalRechazo({ open: false, idPlan: null, comentario: '' })}>
        <DialogTitle>Rechazar plan</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label="Comentario"
            value={modalRechazo.comentario}
            onChange={(e) => setModalRechazo({ ...modalRechazo, comentario: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalRechazo({ open: false, idPlan: null, comentario: '' })}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarRechazo}>Rechazar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalReagendar.open} onClose={cerrarModalReagendar} maxWidth="xs" fullWidth>
        <DialogTitle>
          {modalReagendar.fechaOrigen === modalReagendar.fechaDestino
            ? 'Cambiar jornada'
            : 'Reagendar día'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {modalReagendar.fechaOrigen === modalReagendar.fechaDestino
              ? `Fecha: ${modalReagendar.fechaDestino}`
              : `De ${modalReagendar.fechaOrigen} → ${modalReagendar.fechaDestino}`}
          </Typography>
          <FormControl component="fieldset">
            <FormLabel component="legend">Jornada</FormLabel>
            <RadioGroup
              value={modalReagendar.esSabadoDestino ? 'M' : modalReagendar.tipoJornada}
              onChange={(e) => setModalReagendar((prev) => ({ ...prev, tipoJornada: e.target.value }))}
            >
              <FormControlLabel
                value="C"
                control={<Radio />}
                label="Día completo"
                disabled={
                  modalReagendar.esSabadoDestino
                  || (modalReagendar.tipoOrigen !== 'C' && !modalReagendar.esSabadoOrigen)
                }
              />
              <FormControlLabel value="M" control={<Radio />} label="Medio día (AM)" />
              <FormControlLabel
                value="T"
                control={<Radio />}
                label="Medio día (PM)"
                disabled={modalReagendar.esSabadoDestino}
              />
            </RadioGroup>
          </FormControl>
          {modalReagendar.esSabadoDestino && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              El sábado solo admite medio día AM.
            </Typography>
          )}
          {modalReagendar.esSabadoOrigen && !modalReagendar.esSabadoDestino && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Puede pasar el sábado (medio día) a día completo en un día hábil, si hay saldo disponible.
            </Typography>
          )}
          {modalReagendar.tipoOrigen !== 'C' && !modalReagendar.esSabadoOrigen && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              Este día es medio día: no puede pasarlo a completo (no se aumenta el plan).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModalReagendar}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarReagendar}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <FormPlanVacacionesEmpleadoPdf
        data={printPayload?.data ?? []}
        plan={printPayload?.plan ?? null}
        print={printReporte}
        setPrint={(value) => {
          setPrintReporte(value);
          if (!value) setPrintPayload(null);
        }}
      />
      <FormReporteDepartamentoPlanVacacionPdf
        reporte={printReporteDeptoPayload}
        print={printReporteDepto}
        setPrint={(value) => {
          setPrintReporteDepto(value);
          if (!value) setPrintReporteDeptoPayload(null);
        }}
      />
    </Grid>
  );
}
